import {FormControl, Label} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn, useController} from 'react-hook-form';

import {sort} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../hooks/useCatalogue';
import i18n from '../../../../i18n/i18n';
import {FilterFormData} from '../../../../types/FilterFormData';
import {FeaturesFilterDialog} from './FeaturesFilterDialog';
import {FeaturesFilterTrigger} from './FeaturesFilterTrigger';

interface FeaturesFilterFieldProps extends TestIdProps {
  formControl: FormControl<FilterFormData>;
  formApi: UseFormReturn<FilterFormData>;
}

const EMPTY_FEATURES: string[] = [];

export function FeaturesFilterField(props: FeaturesFilterFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<string[]>(EMPTY_FEATURES);
  const {getFeatureLabel, isLoading} = useCatalogue();
  const {field} = useController({
    name: 'features',
    control: props.formApi.control,
  });
  const features = field.value ?? EMPTY_FEATURES;

  const sortedFeatureItems = sort(
    (left, right) => getFeatureLabel(left).localeCompare(getFeatureLabel(right)),
    features
  ).map((featureKey) => ({key: featureKey, label: getFeatureLabel(featureKey)}));

  const updateFeatures = (value: string[]) => {
    field.onChange(value);
    props.formControl.onChange?.();
  };

  const openDialog = () => {
    if (isLoading) {
      return;
    }

    setDraftValue(features);
    setIsOpen(true);
  };

  const confirmSelection = (value: string[]) => {
    updateFeatures(value);
    setIsOpen(false);
  };

  return (
    <VStack>
      <Label data-testid={suffixTestId('label', props)}>
        {i18n.t('entity.filterForm.features')}
      </Label>
      <FeaturesFilterTrigger
        items={sortedFeatureItems}
        onOpen={openDialog}
        onChange={updateFeatures}
        isDisabled={isLoading}
        isLoading={isLoading}
        data-testid={suffixTestId('featuresFilterTrigger', props)}
      />
      <FeaturesFilterDialog
        isOpen={isOpen}
        draftValue={draftValue}
        onDraftChange={setDraftValue}
        isLoading={isLoading}
        onClose={() => setIsOpen(false)}
        onConfirm={confirmSelection}
        data-testid={suffixTestId('dialog', props)}
      />
    </VStack>
  );
}
